// My Learning (Phase 4a) — the employee's assigned courses + self-paced progress.
import { useState, useEffect } from 'react';
import { GraduationCap, Clock, Award, CalendarDays, MapPin } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, Pill, OutlineBtn, PrimaryBtn } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';
import { printCertificate } from './lib/print';

const STEPS = [0, 25, 50, 75, 100];
const tone = (s) => (s === 'Completed' ? 'success' : s === 'InProgress' ? 'info' : 'warning');
const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; } };

export default function MyLearning({ showToast }) {
  const [rows, setRows] = useState(null);
  const [sessions, setSessions] = useState(null);
  const load = () => api.myCourses().then(setRows).catch(() => setRows([]));
  const loadSessions = () => api.mySessions().then(setSessions).catch(() => setSessions([]));
  useEffect(() => { load(); loadSessions(); }, []);

  const register = async (s) => { try { await api.registerSession(s.id); showToast?.(`Registered for "${s.title}"`); loadSessions(); } catch (e) { showToast?.(e.message); } };
  const unregister = async (s) => { try { await api.unregisterSession(s.id); loadSessions(); } catch (e) { showToast?.(e.message); } };

  const setProgress = async (id, progress) => {
    try { await api.updateMyProgress(id, progress); if (progress === 100) showToast?.('Course completed 🎉'); load(); }
    catch (e) { showToast?.(e.message); }
  };
  const certificate = async (id) => {
    try { printCertificate(await api.courseCertificate(id)); }
    catch (e) { showToast?.(e.message); }
  };

  if (!rows) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="My Learning" sub="Assigned training and your progress" />
      {rows.length === 0 ? (
        <Card><EmptyState icon={GraduationCap} title="No courses yet" desc="Training assigned to you will appear here." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((c) => (
            <Card key={c.id} style={{ padding: 16 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, color: AG.text }}>{c.title} {c.mandatory && <Pill tone="error">Mandatory</Pill>}</div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{c.category}{c.provider ? ` · ${c.provider}` : ''} · <Clock size={11} style={{ display: 'inline', verticalAlign: -1 }} /> {c.durationHours}h</div>
                </div>
                <Pill tone={tone(c.status)} dot>{c.status}</Pill>
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={{ height: 7, borderRadius: 6, background: AG.n100, overflow: 'hidden' }}>
                  <div style={{ width: `${c.progress}%`, height: '100%', background: AG.primary, transition: 'width .3s' }} />
                </div>
                <div className="flex items-center justify-between" style={{ marginTop: 8 }}>
                  <span style={{ fontSize: 12, color: AG.n500 }}>{c.progress}% complete</span>
                  {c.status === 'Completed' ? (
                    <OutlineBtn onClick={() => certificate(c.id)}><Award size={13} style={{ marginRight: 5 }} /> Certificate</OutlineBtn>
                  ) : (
                    <label style={{ fontSize: 12, color: AG.n600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      Update
                      <select value={c.progress} onChange={(e) => setProgress(c.id, Number(e.target.value))} className="ag-input" style={{ width: 90, padding: '4px 8px' }}>
                        {STEPS.map((s) => <option key={s} value={s}>{s}%</option>)}
                      </select>
                    </label>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Training calendar — self-registration (3.x) */}
      <SectionTitle title="Upcoming training" sub="Register for scheduled sessions" />
      {!sessions ? <CenterSpinner /> : sessions.length === 0 ? (
        <Card><EmptyState icon={CalendarDays} title="No upcoming sessions" desc="Scheduled training sessions you can join will appear here." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {sessions.map((s) => {
            const full = s.seatsLeft === 0;
            return (
              <Card key={s.id} style={{ padding: 16 }}>
                <div className="flex items-start justify-between" style={{ gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: AG.text }}>{s.title}</div>
                    <div style={{ fontSize: 12.5, color: AG.n500, marginTop: 3 }}><CalendarDays size={11} style={{ display: 'inline', verticalAlign: -1 }} /> {fmtWhen(s.startsAt)}{s.instructorName ? ` · ${s.instructorName}` : ''}</div>
                    {s.location && <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}><MapPin size={11} style={{ display: 'inline', verticalAlign: -1 }} /> {s.location}</div>}
                    <div style={{ fontSize: 12, color: AG.n400, marginTop: 3 }}>{s.capacity ? `${s.seatsLeft} seat(s) left` : 'Unlimited seats'}</div>
                  </div>
                  {s.myStatus && s.myStatus !== 'Cancelled' ? <Pill tone="success" dot>{s.myStatus}</Pill> : full ? <Pill tone="neutral">Full</Pill> : null}
                </div>
                <div className="flex items-center justify-end" style={{ marginTop: 12 }}>
                  {s.myStatus && s.myStatus !== 'Cancelled' ? (
                    <OutlineBtn onClick={() => unregister(s)}>Cancel registration</OutlineBtn>
                  ) : (
                    <PrimaryBtn onClick={() => register(s)} disabled={full}>{full ? 'Full' : 'Register'}</PrimaryBtn>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
